import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { img, useApi } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const Arrow = ({ dir, onClick }) => (
  <button onClick={onClick} aria-label={dir === "prev" ? "Previous slide" : "Next slide"} data-testid={`hero-slide-${dir}`}
    className={`absolute bottom-4 z-20 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-forest shadow-lg transition-transform duration-200 hover:scale-110 sm:bottom-auto sm:top-1/2 sm:h-14 sm:w-14 sm:-translate-y-1/2 ${dir === "prev" ? "left-3 sm:left-6" : "right-3 sm:right-6"}`}>
    {dir === "prev" ? <ChevronLeft className="h-6 w-6" /> : <ChevronRight className="h-6 w-6" />}
  </button>
);

const HeroButton = ({ url, label, primary, testId }) => {
  const { to } = useLang();
  if (!url || !label) return null;
  const cls = primary ? "btn-primary !bg-white !text-forest hover:!bg-sand" : "inline-flex items-center gap-2 rounded-full border-2 border-white/80 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/15";
  const body = <>{label} {primary && <ArrowRight className="h-4 w-4" />}</>;
  return /^https?:\/\//.test(url)
    ? <a href={url} target="_blank" rel="noreferrer" className={cls} data-testid={testId}>{body}</a>
    : <Link to={to(url.startsWith("/") ? url : `/${url}`)} className={cls} data-testid={testId}>{body}</Link>;
};

const MainSlide = ({ s }) => {
  const { t, L } = useLang();
  return (
    <>
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-sand">{t("hero.eyebrow")}</p>
      <h1 className="mt-4 max-w-4xl font-bebas text-5xl font-normal leading-[0.92] tracking-wide text-white sm:text-6xl lg:text-8xl" data-testid="hero-title">{L(s, "hero_title") || "Serenity Found"}</h1>
      <p className="mt-6 max-w-2xl text-base font-medium leading-relaxed text-white/85 md:text-lg">{L(s, "hero_subtitle")}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <HeroButton primary url={s?.hero_cta1_url} label={L(s, "hero_cta1_label")} testId="hero-cta-homes" />
        <HeroButton url={s?.hero_cta2_url} label={L(s, "hero_cta2_label")} testId="hero-cta-stay" />
      </div>
    </>
  );
};

const Caption = ({ slide }) => {
  const { L } = useLang();
  return (
    <>
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-sand">Escamequita · Nicaragua</p>
      <h2 className="mt-4 max-w-4xl font-bebas text-5xl font-normal leading-[0.92] tracking-wide text-white sm:text-6xl lg:text-8xl" data-testid="hero-slide-title">{L(slide, "title")}</h2>
      {slide.cta_url && (
        <a href={slide.cta_url} target="_blank" rel="noreferrer" className="btn-primary mt-8 !bg-white !text-forest hover:!bg-sand" data-testid={`hero-slide-cta-${slide.id}`}>
          {L(slide, "cta_label") || "Learn more"} <ArrowUpRight className="h-4 w-4" />
        </a>
      )}
    </>
  );
};

export const HeroSlideshow = ({ s }) => {
  const { data: extra } = useApi("/slides", []);
  const slides = [{ id: "main", image: s?.hero_image || "/media/hero.jpg" }, ...(extra || [])];
  const n = slides.length;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((d) => setI((c) => (c + d + n) % n), [n]);

  const ms = Math.min(20, Math.max(3, Number(s?.slide_seconds) || 6)) * 1000;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => go(1), ms);
    return () => clearInterval(id);
  }, [paused, go, ms]);

  const cur = slides[i];
  return (
    <section className="px-3 pt-3 sm:px-5 sm:pt-5" data-testid="home-hero" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative h-[86vh] min-h-[560px] overflow-hidden rounded-3xl bg-forest">
        <AnimatePresence>
          <motion.img key={cur.id} src={img(cur.image)} alt={cur.title || "Escamequita valley near San Juan del Sur, Nicaragua"}
            initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.1, ease: "easeOut" }}
            className="absolute inset-0 h-full w-full object-cover" data-testid={`hero-slide-${cur.id}`} />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
        <div className="relative z-10 flex h-full flex-col justify-end px-6 pb-16 sm:px-20 sm:pb-20 lg:px-24">
          <AnimatePresence mode="wait">
            <motion.div key={cur.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5 }}>
              {cur.id === "main" ? <MainSlide s={s} /> : <Caption slide={cur} />}
            </motion.div>
          </AnimatePresence>
        </div>
        <Arrow dir="prev" onClick={() => go(-1)} />
        <Arrow dir="next" onClick={() => go(1)} />
        <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2" data-testid="hero-slide-dots">
          {slides.map((d, k) => (
            <button key={d.id} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`} data-testid={`hero-slide-dot-${k}`}
              className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-8 bg-white" : "w-2.5 bg-white/50 hover:bg-white/80"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
